<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { formatMetricValue } from '../helpers/reportChartHelpers';

const props = defineProps({
  // One drilldown payload entry: the event's attribution records and its properties.
  event: {
    type: Object,
    required: true,
  },
  // Heads cards whose event isn't attached to a conversation, such as webhook or API events.
  eventName: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const route = useRoute();

const REFERENCE_ICONS = {
  conversation: 'i-lucide-message-circle',
  contact: 'i-lucide-contact',
  agent: 'i-lucide-user-round',
  inbox: 'i-lucide-inbox',
};

const recordPath = (type, record) => {
  const { accountId } = route.params;
  if (type === 'conversation') {
    return frontendURL(conversationUrl({ accountId, id: record.display_id }));
  }

  const paths = {
    contact: `accounts/${accountId}/contacts/${record.id}`,
    agent: `accounts/${accountId}/reports/agents/${record.id}`,
    inbox: `accounts/${accountId}/inbox/${record.id}`,
  };
  return frontendURL(paths[type]);
};

// A contact can be saved without a name, so fall back to its id.
const recordLabel = (type, record) =>
  type === 'conversation'
    ? `#${record.display_id}`
    : record.name || `#${record.id}`;

const conversation = computed(() => props.event.conversation);
const conversationPath = computed(() =>
  conversation.value ? recordPath('conversation', conversation.value) : ''
);

const occurredAt = computed(() =>
  shortTimestamp(dynamicTime(props.event.occurred_at)).trim()
);
const occurredAtTooltip = computed(() =>
  t('WGPT_CUSTOM_REPORTS.DRILLDOWN.OCCURRED_AT', {
    time: format(fromUnixTime(props.event.occurred_at), 'dd MMM yyyy, h:mm a'),
  })
);

const propertyValue = property => {
  if (property.record) return recordLabel(property.type, property.record);
  if (property.type === 'boolean') {
    return property.value
      ? t('WGPT_CUSTOM_REPORTS.DRILLDOWN.YES')
      : t('WGPT_CUSTOM_REPORTS.DRILLDOWN.NO');
  }
  if (property.type === 'number') return formatMetricValue(property.value);

  return String(property.value);
};

const properties = computed(() =>
  props.event.properties.map(property => ({
    key: property.key,
    label: property.label,
    value: propertyValue(property),
    icon: REFERENCE_ICONS[property.type],
    path: property.record ? recordPath(property.type, property.record) : '',
  }))
);

const attribution = computed(() =>
  ['contact', 'inbox', 'agent']
    .filter(type => props.event[type])
    .map(type => ({
      key: type,
      icon: REFERENCE_ICONS[type],
      label: recordLabel(type, props.event[type]),
      path: recordPath(type, props.event[type]),
    }))
);

const linkAttributes = path =>
  path ? { href: path, target: '_blank', rel: 'noopener noreferrer' } : {};

const openConversation = () => {
  if (!conversationPath.value) return;

  window.open(conversationPath.value, '_blank', 'noopener,noreferrer');
};
</script>

<template>
  <article
    class="rounded-md border border-n-weak bg-n-solid-2 p-3"
    :class="{
      'cursor-pointer hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand':
        conversationPath,
    }"
    :role="conversationPath ? 'link' : undefined"
    :tabindex="conversationPath ? 0 : undefined"
    @click="openConversation"
    @keydown.enter.self.prevent="openConversation"
    @keydown.space.self.prevent="openConversation"
  >
    <div class="flex items-start justify-between gap-2">
      <div
        class="flex min-w-0 items-center gap-2 text-sm font-medium leading-5 text-n-slate-12"
      >
        <template v-if="conversation">
          <span>{{ recordLabel('conversation', conversation) }}</span>
          <span
            class="rounded bg-n-alpha-2 px-1.5 py-0.5 text-xs capitalize text-n-slate-11"
          >
            {{ conversation.status }}
          </span>
        </template>
        <span v-else class="truncate">{{ eventName }}</span>
      </div>
      <span
        v-tooltip.left="occurredAtTooltip"
        :aria-label="occurredAtTooltip"
        class="shrink-0 whitespace-nowrap text-xs leading-4 text-n-slate-10"
      >
        {{ occurredAt }}
      </span>
    </div>

    <dl
      v-if="properties.length"
      class="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm"
    >
      <div v-for="property in properties" :key="property.key" class="min-w-0">
        <dt class="truncate text-xs text-n-slate-10">{{ property.label }}</dt>
        <dd class="min-w-0">
          <a
            v-if="property.path"
            v-bind="linkAttributes(property.path)"
            class="group flex min-w-0 items-center gap-1 text-n-slate-12 hover:text-n-blue-11 hover:underline"
            @click.stop
          >
            <Icon
              :icon="property.icon"
              class="size-3 shrink-0 text-n-slate-9 group-hover:text-n-blue-11"
            />
            <span class="truncate">{{ property.value }}</span>
          </a>
          <span v-else class="block truncate text-n-slate-12">
            {{ property.value }}
          </span>
        </dd>
      </div>
    </dl>

    <div v-if="attribution.length" class="mt-2 grid grid-cols-3 gap-2">
      <a
        v-for="item in attribution"
        :key="item.key"
        v-bind="linkAttributes(item.path)"
        class="group flex min-w-0 items-center gap-1 text-body-main text-n-slate-10 hover:text-n-blue-11 hover:underline"
        @click.stop
      >
        <Icon
          :icon="item.icon"
          class="size-3 shrink-0 text-n-slate-9 group-hover:text-n-blue-11"
        />
        <span class="truncate">{{ item.label }}</span>
      </a>
    </div>
  </article>
</template>
